import { Loader2 } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/frontend/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/frontend/components/ui/table";
import { CopyField } from "@/frontend/components/ui/copy-field";
import type { SandboxCredRow, SandboxCredentialsDialogProps } from "@/frontend/types/sandbox-credentials";

/** Modal listing every sandbox test account for a provider (username + password) with per-cell copy-to-clipboard. Continue hands off to the parent's OAuth start. */
export function SandboxCredentialsDialog({ open, onOpenChange, creds, connecting, onProceed }: SandboxCredentialsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{creds.displayName} test credentials</DialogTitle>
          {creds.helperText && (
            <DialogDescription>{creds.helperText}</DialogDescription>
          )}
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto">
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="h-9 py-2 w-1/2">Username</TableHead>
                <TableHead className="h-9 py-2 w-1/2">Password</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {creds.rows.map((row) => (
                <SandboxRow key={row.username} row={row} />
              ))}
            </TableBody>
          </Table>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onProceed} disabled={connecting}>
            {connecting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : null}
            Continue to {creds.displayName.replace(/\s+Sandbox$/i, "")} sign-in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One row in the sandbox credentials table — a username chip and a password chip, each with its own copy control. */
function SandboxRow({ row }: { row: SandboxCredRow }) {
  return (
    <TableRow>
      <TableCell className="align-top py-2 whitespace-nowrap">
        <CopyField variant="chip" value={row.username} />
      </TableCell>
      <TableCell className="align-top py-2 whitespace-nowrap">
        <CopyField variant="chip" value={row.password} />
      </TableCell>
    </TableRow>
  );
}

